<template>
  <div>
    <div>
      <p>Select your enrollment preference below. If you opt for paperless enrollment you will start receiving official DMV notices by email instead of paper notices by US mail.
      </p>
      <p>
        <b>NOTE:</b> RI DMV is implementing paperless notices in a phased manner. After you opt-in for paperless communication you may still receive some RI DMV notices by paper.
      </p>

      <div class="app-input-group">
        <div class="app-comm-info">
          <div class="app-input-selector">
            <input type="radio" id="mailingAddress" v-model="communication" value="1" name="communication">
            <label for="mailingAddress">Paper notices by US mail at the following mailing address</label>
          </div>
          <div class="user-info" v-if="personalInfo?.personalInformation?.mailingAddress">
            <span class="user-address-info">Mailing address</span>
            <span>{{ personalInfo.personalInformation.mailingAddress?.addressLine1 }} </span>
            <span>{{ personalInfo.personalInformation.mailingAddress?.addressLine2 }}</span>
            <span>
              {{ personalInfo.personalInformation.mailingAddress?.city }},
              {{
                  personalInfo.personalInformation.mailingAddress?.addressType === 'Us Domestic' ?
                      personalInfo.personalInformation.mailingAddress?.state :
                      personalInfo.personalInformation.mailingAddress?.province
              }},
              {{ personalInfo.personalInformation.mailingAddress?.country }}
            </span>
            <span>
              {{
                  personalInfo.personalInformation.mailingAddress?.addressType === 'Us Domestic' ?
                      personalInfo.personalInformation.mailingAddress?.zipPlus === '' ? personalInfo.personalInformation.mailingAddress?.zipCode :
                      `${personalInfo.personalInformation.mailingAddress?.zipCode} - ${personalInfo.personalInformation.mailingAddress?.zipPlus}`
                      : `${personalInfo.personalInformation.mailingAddress?.internationalZipCode}`
              }}
            </span>
          </div>
        </div>
        <div class="app-comm-info">
          <div class="app-input-selector">
            <input type="radio" id="contactInfo" v-model="communication" value="2" name="communication">
            <label for="contactInfo">Paperless notices at the following email address</label>
          </div>
          <div class="user-info">
            <span class="contact-information">Contact information</span>
            <div class="contact-display">
              <span class="user-info-email">{{ currentEmail }}</span>
              <img v-if="isEmailVerified" class="verify-icon" src="static/verified-icon.png" alt="Email is verified">
            </div>
          </div>
        </div>
      </div>

      <AsideForCommunication :verify="termsAccepted" @doResetVerify="termsAccepted = false" @doValidated="routeToNext"/>
    </div>
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <button type="submit" class="app-btn-primary" @click="handleRouting('next')">  
        Submit
      </button>
    </BaseButtonGroup>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'; 
import { useStore } from 'vuex'; 
import { useRouter } from 'vue-router'; 
import * as COMMONS from '../../store/_commons'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import AsideForCommunication from '../confirmation/AsideForCommunication.vue';

const termsAccepted = ref(false);
const communication = ref('1');

// Vuex Store
const store = useStore(); 

// Vue Router 
const router = useRouter(); 

// Computed Properties 
const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]); 
const isEmailVerified = computed(() => !!personalInfo.value?.personalInformation?.isEmailVerified);
const currentEmail = computed(() => personalInfo.value?.personalInformation?.email || 'No email on file');

// Lifecycle Hooks 
onMounted(async () => { 
  window.scroll(0, 0);
  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");
  document.getElementById("app").scrollTop = 0;

  const currentEnrollmentStatus = personalInfo.value?.paperlessEnrollmentInformation?.paperlessEnrollment;

  // Map current status to the correct radio option
  communication.value = currentEnrollmentStatus === 'Yes' ? '2' : '1';
});

//Methods
const handleRouting = async (info) => {
  if (info == 'previous') {
    router.push({ path: '/profile' });
  } else if (info == 'next') {
    // have child component initiate checkbox validation
    // actual routing is determined by child's emit (to @doValidated)
    termsAccepted.value = true;
  }
};

const routeToNext = async () => {
  if (!termsAccepted.value) return;

  try {
    const currentEnrollmentStatus = personalInfo.value?.paperlessEnrollmentInformation?.paperlessEnrollment;

    const selectedPaperless = communication.value === '2';

    const updateEnrollmentStatus = (currentEnrollmentStatus === 'No' && selectedPaperless) || (currentEnrollmentStatus === 'Yes' && !selectedPaperless);

    if(updateEnrollmentStatus) {
      const attributes = [
        {
          customerAttributeTypeId: 1, // Paperless enrollment attribute
          customerAttributeStatus: selectedPaperless
        }
      ];
      await store.dispatch(COMMONS.GUEST_UPDATE_ATTRIBUTE, attributes);
    }
    router.push({ path: '/profile' });
  } catch (error) {
    console.error("Navigation stopped due to API error.", error);
  }
};
</script>

<style lang="scss" scoped>
.app-input-group {
  display: flex;
  margin-left: 1rem;
  .app-comm-info {
    margin-right: 2rem;
  }
  .app-input-selector {
    align-items: baseline;
  }
  .user-info {
    word-wrap: break-word;    
    .user-address-info, .contact-information {
      font-weight: 600;

    }
    .contact-display {
      display: flex;
      .verify-icon {
        width: 50px;
        height: 20px;
        vertical-align: middle;
        margin-left: 10px;
      }
    }
    span {
      display: block;
      line-height: 30px;
    }

  }
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

@media only screen and (max-width: 600px) {
  .app-input-group {
    display: block;
    margin-left: 1rem;
    .app-comm-info {
      margin-right: 2rem;
    }
    .app-input-selector {
      align-items: baseline;
    }
    .user-info {
      word-wrap: break-word;    
      .user-address-info, .contact-info {
        font-weight: 600;
      }
      span {
        display: block;
        line-height: 30px;
      }
    }
  }
}

</style>